//frontend/src/pages/auth/Login.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  TextField, 
  Button, 
  IconButton, 
  InputAdornment,
  Paper,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  TranslateOutlined,
  EmailOutlined,
  LockOutlined 
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import ForgotPassword from '../../components/auth/ForgotPassword';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
});

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyTwoFactor } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  // Wave-12 Phase 1 · TOTP challenge state. When the backend returns
  // { twofaRequired: true, challengeToken }, we swap the form to a
  // "enter 6-digit code" step. The challenge JWT lives 5 minutes.
  const [twofa, setTwofa] = useState({ required: false, challengeToken: null, code: '' });

  const from = location.state?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Handle password reset success message
  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message);
      // Clear the message from location state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');

    const result = await login(data);

    if (result.twofaRequired) {
      // Wave-12 · pivot to step 2. Password succeeded; challenge issued.
      setTwofa({ required: true, challengeToken: result.challengeToken, code: '' });
      setIsLoading(false);
      return;
    }
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
      setIsLoading(false);
    }
  };

  const onSubmitTotp = async (e) => {
    e.preventDefault();
    setError('');
    const raw = String(twofa.code || '').trim();
    if (!raw) return setError('Enter your 6-digit code or a backup code');
    setIsLoading(true);
    const r = await verifyTwoFactor({ challengeToken: twofa.challengeToken, code: raw });
    if (r.success) {
      navigate(from, { replace: true });
    } else {
      setError(r.error);
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="w-full max-w-md"
      >
        <motion.div variants={itemVariants} className="text-center mb-8">
           <div className="inline-flex items-center justify-center mb-1" style={{ width: '320px', height: '100px' }}>
            <img
              src="/Lingo24.svg"
              alt="MTX"
              className="w-full h-full object-contain"
            />
          </div>
          <Typography variant="h6" className="font-bold text-gray-900">
            Welcome
          </Typography>
          <Typography variant="body1" className="text-gray-600 mt-2">
            Sign in to MTX Portal
          </Typography>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Paper elevation={0} className="p-8 border border-gray-200">
            {error && (
              <Alert severity="error" className="mb-4">
                {error}
              </Alert>
            )}

            {twofa.required ? (
              <form onSubmit={onSubmitTotp} className="space-y-6">
                <Alert severity="info" sx={{ mb: 2 }}>
                  Two-factor authentication is enabled on this account. Enter the 6-digit code from
                  your authenticator app (or a backup code).
                </Alert>
                <TextField
                  autoFocus
                  fullWidth
                  label="Authenticator code"
                  value={twofa.code}
                  onChange={(e) => setTwofa({ ...twofa, code: e.target.value.trim() })}
                  placeholder="123456"
                  inputProps={{
                    // Wider on the digits themselves (letter-spacing), but the
                    // placeholder inherits the spacing too — so use a shorter
                    // spacing and slightly smaller font to keep "123456" tidy
                    // inside the field. Max 20 chars fits backup-code format
                    // XXXXX-XXXXX (11 chars) with a little slack.
                    maxLength: 20,
                    style: { fontFamily: 'monospace', fontSize: 20, textAlign: 'center', letterSpacing: 3 },
                    inputMode: 'text',
                    autoComplete: 'one-time-code',
                  }}
                />
                <Button type="submit" fullWidth variant="contained" size="large" disabled={isLoading} className="py-3">
                  {isLoading ? 'Verifying…' : 'Verify & sign in'}
                </Button>
                <Button
                  fullWidth
                  size="small"
                  onClick={() => { setTwofa({ required: false, challengeToken: null, code: '' }); setError(''); }}
                  sx={{ textTransform: 'none', mt: 1 }}
                >
                  ← Back to password
                </Button>
              </form>
            ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <TextField
                {...register('email')}
                label="Email Address"
                type="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined className="text-gray-400" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...register('password')}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined className="text-gray-400" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                className="py-3"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>

              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  color="primary"
                  onClick={() => setForgotPasswordOpen(true)}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Forgot your password?
                </Button>
              </Box>
            </form>
            )}
          </Paper>
        </motion.div>

        <motion.div variants={itemVariants} className="mt-4 text-center text-sm text-gray-600">
          <span>Don't have an account?</span>{' '}
          <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-medium">
            Sign up for free
          </Link>
        </motion.div>

        {/* Footer Section */}
        <motion.div variants={itemVariants} className="mt-8 text-center text-xs text-gray-500">
          <div><strong>MTX</strong> powered by <strong>DLX Technology</strong></div>
          <div>&copy;{new Date().getFullYear()} DLX Technology Co., Ltd</div>
        </motion.div>
      </motion.div>

      {/* Forgot Password Dialog */}
      <ForgotPassword 
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
      />
    </div>
  );
};

export default Login;